import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useAppStore } from '../../app/hooks/useAppStore';
import { ACHIEVEMENTS, AchievementCheckData } from '../progress/AchievementsSection';
import { AchievementUnlockModal, AchievementData } from './AchievementUnlockModal';
import { MODULES } from '../../content';
export function AchievementCeremony() {
  const {
    state,
    activeAccount,
    getModuleMasteryForGroup,
    getAnsweredQuestions,
    markAchievementUnlocked
  } = useAppStore();
  const [activeAchievement, setActiveAchievement] = useState<AchievementData | null>(null);
  const [queue, setQueue] = useState<AchievementData[]>([]);
  // Baseline ref: tracks which account we've baselined.
  // On first render per account, we silently mark all already-qualifying
  // achievements without showing modals. This prevents the "5 popups on load"
  // problem when switching to demo accounts or loading fresh accounts.
  const baselinedAccountRef = useRef<string | null>(null);
  // Only run for learners
  const isLearner = activeAccount?.role === 'learner';
  const accountId = activeAccount?.id ?? null;
  // Calculate stats needed for achievements
  const {
    masteredCount,
    totalQuestionsAnswered
  } = useMemo(() => {
    if (!isLearner || !state) return {
      masteredCount: 0,
      totalQuestionsAnswered: 0
    };
    let mastered = 0;
    let totalQs = 0;
    MODULES.forEach(m => {
      const mastery = getModuleMasteryForGroup(m.id);
      const answered = getAnsweredQuestions(m.id).length;
      totalQs += answered;
      if (mastery >= 95) mastered++;
    });
    return {
      masteredCount: mastered,
      totalQuestionsAnswered: totalQs
    };
  }, [state, isLearner, getModuleMasteryForGroup, getAnsweredQuestions]);
  // Derive specific values to avoid re-running on every state change
  const level = state?.stats?.level ?? 0;
  const xp = state?.stats?.xp ?? 0;
  const unlockedCount = state?.stats?.unlockedAchievements?.length ?? 0;
  const currentWeekDays = state?.dailyStreak?.currentWeekDays || 0;
  const consecutiveFullWeeks = state?.dailyStreak?.consecutiveFullWeeks || 0;
  const longestTotalStreak = state?.streak?.longestTotalStreak || 0;
  const totalDailyGoalsReached = state?.dailyStreak?.totalDailyGoalsReached ?? (state?.dailyStreak?.weeklyHistory ? Object.values(state.dailyStreak.weeklyHistory).reduce((a, b) => a + b, 0) : 0);
  const totalPerfectSessions = state?.dailyStreak?.totalPerfectSessions ?? 0;
  // Reset baseline when account changes (clear queue too)
  useEffect(() => {
    if (accountId !== baselinedAccountRef.current) {
      setQueue([]);
      setActiveAchievement(null);
    }
  }, [accountId]);
  useEffect(() => {
    if (!isLearner || !state) return;
    const checkData: AchievementCheckData = {
      totalQuestionsAnswered,
      masteredCount,
      level,
      xp,
      currentWeekDays,
      consecutiveFullWeeks,
      longestTotalStreak,
      totalDailyGoalsReached,
      totalPerfectSessions
    };
    const unlockedIds = state.stats.unlockedAchievements || [];
    // Determine if this is the first check for this account (baseline run)
    const isBaselineRun = baselinedAccountRef.current !== accountId;
    const newlyUnlocked: AchievementData[] = [];
    ACHIEVEMENTS.forEach(achievement => {
      if (!unlockedIds.includes(achievement.id) && achievement.check(checkData)) {
        // Mark as unlocked immediately to prevent multiple triggers
        markAchievementUnlocked(achievement.id);
        // Only queue modal if this is NOT the baseline run
        // (baseline = first check after account load/switch — silently mark existing achievements)
        if (!isBaselineRun) {
          newlyUnlocked.push({
            id: achievement.id,
            name: achievement.name,
            description: achievement.description,
            emoji: achievement.emoji,
            rarity: achievement.rarity
          });
        }
      }
    });
    // Mark baseline as done for this account
    if (isBaselineRun) {
      baselinedAccountRef.current = accountId;
    }
    if (newlyUnlocked.length > 0) {
      setQueue(prev => [...prev, ...newlyUnlocked]);
    }
  }, [isLearner, accountId, totalQuestionsAnswered, masteredCount, level, xp, unlockedCount, currentWeekDays, consecutiveFullWeeks, longestTotalStreak, totalDailyGoalsReached, totalPerfectSessions, markAchievementUnlocked]);
  // Process queue
  useEffect(() => {
    if (!activeAchievement && queue.length > 0) {
      setActiveAchievement(queue[0]);
      setQueue(prev => prev.slice(1));
    }
  }, [queue, activeAchievement]);
  const handleClose = () => {
    setActiveAchievement(null);
  };
  return <AchievementUnlockModal achievement={activeAchievement} onClose={handleClose} data-id="element-2028" />;
}